/* Real-mode Claude call — batched, parallel, with retry.
 *
 * Output schema per question:
 *   {
 *     id:                 number,
 *     answer:             string,   // response text written to the Excel cell
 *     status:             "Met" | "Partial" | "Gap",   // requirement fitment
 *     status_rationale:   string,   // 1 sentence: why this status
 *     confidence:         number,   // 0..1 — certainty in the answer's accuracy
 *     confidence_rationale: string, // 1 sentence: what drove the confidence
 *     citations:          string[]  // dotted RKB paths (e.g. "acumatica.certifications.socii")
 *   }
 *
 * Status and confidence are INDEPENDENT:
 *   - High-confidence Gap    → agent is sure the product does NOT meet the req. No review needed.
 *   - Low-confidence Met     → agent thinks it meets, but RKB was vague. Review needed.
 *   - Review queue is driven by confidence, not status.
 */

import { WMS_RKB } from './rkb-data.js';
import { claudeProxy } from './claude-proxy.js';
import { PRODUCT } from './products.js';
import { DEFAULT_MODEL } from './models.js';
import {
  DEFAULT_VOCAB, labelsOf, matchStatus, gapLabel, labelForRole, roleOf, emptyTally,
} from './status-vocab.js';

/** One-line descriptions for the RKB's top-level sections. */
const RKB_KEY_NOTES = {
  rkb_meta: 'version, last_updated, owner',
  wm_synergy: 'shared company info, references policy, compliance posture, escalation rules',
  acumatica: 'Acumatica product capabilities, certifications, integrations, limitations',
  infor_csi: 'Infor CSI product capabilities, certifications, integrations, limitations',
  infor_visual: 'Infor VISUAL product capabilities, certifications, integrations, limitations',
  re_man: 'Re-Man (WM Synergy remanufacturing solution on Infor CSI) capabilities, licensing, positioning',
  ishop: 'iShop (WM Synergy B2B/B2C eCommerce storefront + portal for Infor CSI/SyteLine) capabilities, licensing, positioning',
};

/** Render a nested RKB value as indented plain text for the rules block. */
function renderValue(v, indent = '  ') {
  if (v == null) return [];
  if (Array.isArray(v)) return v.flatMap((x) => renderValue(x, indent));
  if (typeof v === 'object') {
    return Object.entries(v).flatMap(([k, val]) => {
      const label = k.replace(/_/g, ' ');
      if (val && typeof val === 'object') {
        return [`${indent}${label}:`, ...renderValue(val, indent + '  ')];
      }
      return [`${indent}${label}: ${val}`];
    });
  }
  return [`${indent}${v}`];
}

/**
 * The rules that outrank the RKB, rendered ABOVE the RKB blob.
 *
 * These live in the data (wm_synergy.rfp_answer_rules, wm_synergy.section_8_rules,
 * <product>.correction_notes) but used to sit buried inside a 300 KB JSON string
 * with nothing marking them as authoritative. Every correction note exists
 * because that question was answered wrong in a live RFP, so hoping the model
 * finds them mid-blob is not a control. Nothing here is hardcoded policy — it is
 * all rendered from the RKB so a KB update changes the rules.
 */
function buildRulesBlock(productKey, productLabel) {
  const wm = WMS_RKB.wm_synergy || {};
  const lines = [
    `# NON-NEGOTIABLE RULES — these override anything in the RKB below`,
    `If a rule here conflicts with the RKB JSON, the rule wins.`,
    ``,
  ];

  const answerRules = Array.isArray(wm.rfp_answer_rules) ? wm.rfp_answer_rules : [];
  if (answerRules.length) {
    lines.push(`## Answer rules`);
    answerRules.forEach((r, i) => {
      lines.push(`${i + 1}. ${r.rule || ''}`);
      if (r.description) lines.push(`   ${r.description}`);
    });
    lines.push(``);
  }

  if (wm.section_8_rules) {
    lines.push(`## Implementation / methodology language (Section 8)`);
    lines.push(...renderValue(wm.section_8_rules));
    lines.push(``);
  }

  const corrections = Array.isArray(WMS_RKB[productKey]?.correction_notes)
    ? WMS_RKB[productKey].correction_notes
    : [];
  if (corrections.length) {
    lines.push(`## Correction notes for ${productLabel} — each of these was answered WRONG in a live RFP`);
    corrections.forEach((c, i) => {
      lines.push(`${i + 1}. ${c.note || ''}${c.date ? ` (recorded ${c.date})` : ''}`);
    });
    lines.push(``);
  }

  lines.push(`## Named modules — be explicit about licensing`);
  lines.push(
    `Where a capability is delivered by a separately licensed module or add-on (for example `
    + `Infor Factory Track, Infor APS, Infor Forecasting, Infor Customer Portal), NAME the module `
    + `in the answer and state whether it is included in the base product or licensed separately. `
    + `Buyers price from these answers, so an unqualified "yes" that depends on an unnamed add-on `
    + `is a misrepresentation.`
  );
  lines.push(``);

  return lines;
}

/**
 * @param product      RKB product key
 * @param opts.vocab   answer vocabulary for this run (defaults to Met/Partial/Gap)
 * @param opts.instructions  extracted RFP-specific response instructions, if any
 */
export function buildSystemPrompt(product, opts = {}) {
  const productKey = product;
  const productLabel = PRODUCT[product]?.prompt || product;
  const vocab = opts.vocab || DEFAULT_VOCAB;
  const instructions = opts.instructions || null;

  const labels = labelsOf(vocab);
  const labelUnion = labels.map((l) => JSON.stringify(l)).join(' | ');
  const metLabel = labelForRole(vocab, 'met');
  const partialLabel = labelForRole(vocab, 'partial');
  const redLabel = gapLabel(vocab);

  const keyLines = Object.keys(WMS_RKB).map(
    (k) => `  ${k.padEnd(14)} — ${RKB_KEY_NOTES[k] || 'RKB section'}`
  );

  const instructionLines = [];
  if (instructions) {
    instructionLines.push(`# RFP-SPECIFIC INSTRUCTIONS — extracted from this workbook`);
    if (instructions.notes) instructionLines.push(`Notes: ${instructions.notes}`);
    if (instructions.word_limits?.per_answer) {
      instructionLines.push(`Answer length limit: ${instructions.word_limits.per_answer} words per answer. Respect it.`);
    }
    if (Array.isArray(instructions.prohibited) && instructions.prohibited.length) {
      instructionLines.push(`This RFP prohibits disclosing: ${instructions.prohibited.join(', ')}.`);
    }
    instructionLines.push(``);
  }

  return [
    `# ROLE`,
    `You are WM Synergy's RFP Response Assistant. WM Synergy is an ERP implementation and managed-services partner specializing in Infor CloudSuite Industrial, Infor VISUAL, Acumatica, and its own Re-Man remanufacturing solution and iShop B2B eCommerce portal, for mid-market manufacturers, distributors, and government contractors. Draft accurate, compliant, professional responses to inbound RFP/RFI questionnaires using ONLY the Reference Knowledge Base (RKB) embedded below.`,
    ``,
    `# PRODUCT CONTEXT`,
    `This RFP is being answered for: ${productLabel} (rkb key: "${productKey}").`,
    `Every capability answer MUST describe ${productLabel} specifically — do not conflate it with any other product in the RKB.`,
    `Shared WM Synergy policies (company info, references, compliance posture, insurance, methodology) apply to every response regardless of product.`,
    ``,
    ...instructionLines,
    ...buildRulesBlock(productKey, productLabel),
    `# REFERENCE KNOWLEDGE BASE`,
    `The RKB is the sole source of truth. It is embedded in full below as JSON.`,
    `Top-level keys:`,
    ...keyLines,
    ``,
    `Use wm_synergy for company-level questions (legal name, HQ, financials, references, insurance, staff counts, methodology).`,
    `Use the product section ("${productKey}") for software capability, architecture, SLA, and integration questions.`,
    ``,
    `<RKB>`,
    JSON.stringify(WMS_RKB),
    `</RKB>`,
    ``,
    `# HARD RULES`,
    `1. Base every answer ONLY on the RKB. If the RKB does not contain the information, do NOT invent it — return status "${redLabel}".`,
    `2. NEVER invent facts, capabilities, dates, names, numbers, or certifications.`,
    `3. NEVER disclose pricing, client names, or proprietary financial data unless wm_synergy.reference_policy explicitly permits it for the field being asked about.`,
    `4. Quote specific RKB values (uptime SLAs, certification levels, version numbers, dates) verbatim — do not paraphrase numerics.`,
    `5. Answer honestly about ${productLabel}. If a question asks about a feature another product in the RKB has but ${productLabel} does not, say so — do not hedge.`,
    `6. Write in third person, factual, concise ("${productLabel} provides…", "WM Synergy supports…"). No marketing adjectives, no emojis, no bullet points unless the answer is genuinely a list.`,
    `7. If a question contains multiple sub-questions, answer each in order within the same answer string.`,
    ``,
    `# STATUS — REQUIREMENT FITMENT (independent of confidence)`,
    `Status is a judgment about the PRODUCT / WM Synergy meeting the requirement. It is NOT a judgment about your own certainty.`,
    ``,
    vocab.source === 'default'
      ? `Use EXACTLY these status values:`
      : `This RFP mandates its own response categories. Use EXACTLY these values — an answer set in a different vocabulary cannot be scored against other vendors:`,
    ``,
    ...vocab.categories.map((c) => {
      const color = { met: 'GREEN', partial: 'YELLOW', gap: 'RED' }[c.role] || 'YELLOW';
      return `"${c.label}" — ${c.definition || 'Use per this RFP’s own definition of the category.'} Excel cell will be ${color}.`;
    }),
    ``,
    `Return the status string EXACTLY as written above — same spelling, same casing, same punctuation.`,
    ``,
    `# CONFIDENCE — CERTAINTY IN YOUR OWN ANSWER (independent of status)`,
    `Confidence is how sure you are that the ANSWER you wrote is correct, given the RKB. A Gap can be high-confidence (RKB clearly says "not supported") or low-confidence (RKB is silent and you are guessing). A Met can be high- or low-confidence for the same reasons.`,
    ``,
    `Confidence bands (return a number 0.00–1.00, two decimals):`,
    `  0.90–1.00 — The RKB directly and verbatim answers the question. You are quoting a specific field.`,
    `  0.75–0.89 — The RKB covers the question with minor inference (e.g. synonym, restating a capability in the question's language). No ambiguity.`,
    `  0.55–0.74 — Inferred from adjacent RKB content. Plausible but not directly stated.`,
    `  0.30–0.54 — RKB has weak or tangential coverage. The answer is a reasonable default but may be wrong.`,
    `  0.00–0.29 — RKB is silent or the question needs customer-specific data. Answer is a [NEEDS YOUR INPUT] placeholder OR a best-guess Gap.`,
    ``,
    `Anything below 0.60 will be routed to the human review queue regardless of status.`,
    ``,
    `# CITATIONS`,
    `For each answer, list the RKB paths you used as dotted keys. Examples: "acumatica.architecture.hosting", "wm_synergy.reference_policy", "infor_csi.certifications". Citations must be real paths from the RKB above. If you relied on nothing (pure Gap with no RKB basis), return an empty array.`,
    ``,
    `# OUTPUT FORMAT — STRICT`,
    `Return ONLY a JSON array. No prose, no markdown fences, no commentary before or after. Every element MUST have every field:`,
    ``,
    `  {`,
    `    "id":                   <integer, exact match of the input id>,`,
    `    "answer":               <string, the response text that will be written into the Excel cell>,`,
    `    "status":               ${labelUnion},`,
    `    "status_rationale":     <string, ≤ 200 chars, one sentence explaining the status>,`,
    `    "confidence":           <number 0.00..1.00, two decimals>,`,
    `    "confidence_rationale": <string, ≤ 200 chars, one sentence naming the signal that drove confidence>,`,
    `    "citations":            [<string>, ...]`,
    `  }`,
    ``,
    `The array MUST contain exactly one object per input question, in the same order as the input.`,
    ``,
    `# EXAMPLES`,
    ``,
    `Example 1 — direct RKB hit, "${metLabel}", high confidence:`,
    `Q: "What is the system uptime SLA?"`,
    `{"id":0,"answer":"${productLabel} is hosted on AWS with a contractually committed 99.95% monthly uptime SLA, measured excluding scheduled maintenance windows communicated at least 72 hours in advance.","status":"${metLabel}","status_rationale":"RKB specifies 99.95% uptime SLA, directly matching the requirement.","confidence":0.93,"confidence_rationale":"Verbatim from rkb.${productKey}.sla.uptime.","citations":["${productKey}.sla.uptime","${productKey}.architecture.hosting"]}`,
    ``,
    `Example 2 — product does not support, "${redLabel}", high confidence:`,
    `Q: "Does the system natively support SAP IDoc inbound integration?"`,
    `{"id":1,"answer":"${productLabel} does not provide native SAP IDoc integration. Third-party middleware (e.g. Celigo, Boomi) is the supported path for IDoc translation to REST.","status":"${redLabel}","status_rationale":"No native IDoc support; integration requires third-party middleware.","confidence":0.88,"confidence_rationale":"RKB integrations list is exhaustive and does not include IDoc.","citations":["${productKey}.integrations.supported_protocols"]}`,
    ``,
    `Example 3 — partially supported, "${partialLabel}", medium confidence:`,
    `Q: "Does the system support advanced finite-capacity scheduling with drag-and-drop visual Gantt?"`,
    `{"id":2,"answer":"${productLabel} includes finite-capacity scheduling. A drag-and-drop visual Gantt is available via an add-on module rather than as native core functionality; refer to the optional APS package for full Gantt interactivity.","status":"${partialLabel}","status_rationale":"Finite-capacity scheduling is native; the drag-and-drop Gantt requires an add-on.","confidence":0.68,"confidence_rationale":"RKB confirms scheduling and APS add-on, but does not fully detail Gantt interactivity.","citations":["${productKey}.manufacturing.scheduling","${productKey}.addons.aps"]}`,
    ``,
    `Example 4 — customer-specific input required, "${redLabel}", low confidence:`,
    `Q: "Provide three client references in the defense sector with contact names and phone numbers."`,
    `{"id":3,"answer":"[NEEDS YOUR INPUT — defense-sector reference contacts] WM Synergy's reference policy requires written client approval before releasing names and contact information. Please coordinate with the account executive to obtain approved references for this opportunity.","status":"${redLabel}","status_rationale":"Customer-specific data not in RKB; blocked by reference policy.","confidence":0.22,"confidence_rationale":"RKB reference_policy is clear but the specific references must come from the account team.","citations":["wm_synergy.reference_policy"]}`,
    ``,
    `Now answer the questions that follow.`,
  ].join('\n');
}

function buildUserBatchPrompt(questions) {
  const lines = ['Answer the following RFP questions. Return ONLY the JSON array.', ''];
  questions.forEach((q) => {
    lines.push(`[id=${q.id}] (section: ${q.sheet}) ${q.question}`);
    // Sibling cells from the same spreadsheet row — business rationale,
    // future-state capability, current system. This is the context that makes
    // an answer specific to the customer rather than generic boilerplate.
    if (q.context?.length) {
      q.context.forEach((c) => lines.push(`    ${c}`));
    }
    lines.push('');
  });
  return lines.join('\n');
}

/**
 * Robust JSON extractor. Handles markdown fences, leading/trailing prose,
 * and (best-effort) trailing commas.
 */
export function extractJsonArray(text) {
  if (typeof text !== 'string') throw new Error('Non-string response');
  let s = text.replace(/```(?:json)?\s*/gi, '').replace(/```/g, '');
  const start = s.indexOf('[');
  const end = s.lastIndexOf(']');
  if (start === -1 || end === -1 || end <= start) {
    throw new Error('No JSON array markers found');
  }
  const candidate = s.slice(start, end + 1);
  try {
    return JSON.parse(candidate);
  } catch {
    const cleaned = candidate.replace(/,(\s*[\]}])/g, '$1');
    return JSON.parse(cleaned);
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Parse an error thrown by claudeProxy.complete and decide if it's worth retrying.
 * /api/claude errors carry the upstream status code in the message, e.g.
 *   "/api/claude 429 — ..."  or  "/api/claude 503 — ..."
 */
function retryableHttpError(err) {
  const m = /\/api\/claude\s+(\d{3})/.exec(err?.message || '');
  if (!m) return false;
  const status = Number(m[1]);
  return status === 429 || (status >= 500 && status < 600);
}

async function callClaudeBatch({ systemPrompt, questions, model, attempt = 1, maxAttempts = 4 }) {
  let resp;
  try {
    resp = await claudeProxy.complete({
      system: systemPrompt,
      messages: [
        { role: 'user', content: buildUserBatchPrompt(questions) },
      ],
      model,
    });
  } catch (e) {
    if (attempt < maxAttempts && retryableHttpError(e)) {
      // Exponential backoff + jitter: 500ms, 1.5s, 3.5s
      const base = 500 * Math.pow(2, attempt - 1);
      const jitter = Math.floor(Math.random() * 400);
      await sleep(base + jitter);
      return callClaudeBatch({ systemPrompt, questions, model, attempt: attempt + 1, maxAttempts });
    }
    throw e;
  }

  try {
    const parsed = extractJsonArray(resp);
    if (!Array.isArray(parsed)) throw new Error('Response is not a JSON array');
    return parsed;
  } catch (e) {
    // Parse errors: retry once (model occasionally wraps in prose).
    if (attempt < 2) {
      return callClaudeBatch({ systemPrompt, questions, model, attempt: attempt + 1, maxAttempts });
    }
    throw new Error('Parse failed: ' + e.message);
  }
}

function normalizeRow(q, raw, vocab, onLog) {
  if (!raw || typeof raw !== 'object') {
    return gapFallback(q, 'missing from model response', vocab);
  }
  // An out-of-vocabulary status used to be coerced to Gap silently, turning a
  // compliant answer into a red cell that skipped the review queue. Keep the
  // fallback, but say so and force the row into review.
  let status = matchStatus(vocab, raw.status);
  let coerced = false;
  if (!status) {
    status = gapLabel(vocab);
    coerced = true;
    onLog?.(`Row ${q.id}: model returned status ${JSON.stringify(raw.status)}, which is not in this run's answer vocabulary — flagged for review`, 'warn');
  }
  let conf =
    typeof raw.confidence === 'number' && isFinite(raw.confidence)
      ? Math.max(0, Math.min(1, Number(raw.confidence.toFixed(2))))
      : 0.3;
  if (coerced) conf = Math.min(conf, 0.4);
  const answer = typeof raw.answer === 'string' && raw.answer.trim()
    ? raw.answer.trim()
    : '[NEEDS YOUR INPUT — empty answer from model]';
  const statusRationale = typeof raw.status_rationale === 'string' ? raw.status_rationale.slice(0, 400) : '';
  const confidenceRationale = typeof raw.confidence_rationale === 'string' ? raw.confidence_rationale.slice(0, 400) : '';
  const citations = Array.isArray(raw.citations) ? raw.citations.filter((c) => typeof c === 'string').slice(0, 8) : [];
  return {
    id: q.id, sheet: q.sheet, row: q.row, col: q.col,
    question: q.question,
    answer, status, confidence: conf,
    status_rationale: statusRationale,
    confidence_rationale: confidenceRationale,
    citations,
  };
}

function gapFallback(q, reason, vocab) {
  return {
    id: q.id, sheet: q.sheet, row: q.row, col: q.col,
    question: q.question,
    answer: `[NEEDS YOUR INPUT — ${reason}]`,
    status: gapLabel(vocab || DEFAULT_VOCAB), confidence: 0.1,
    status_rationale: 'Unable to answer automatically.',
    confidence_rationale: reason,
    citations: [],
  };
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

async function runPool(batches, concurrency, worker) {
  const results = new Array(batches.length);
  let cursor = 0;
  const workers = new Array(Math.min(concurrency, batches.length)).fill(0).map(async () => {
    while (true) {
      const idx = cursor++;
      if (idx >= batches.length) return;
      results[idx] = await worker(batches[idx], idx);
    }
  });
  await Promise.all(workers);
  return results;
}

export async function realFill(opts) {
  const {
    product, questions,
    model = DEFAULT_MODEL,
    batchSize = 6, concurrency = 4,
    vocab = DEFAULT_VOCAB,
    instructions = null,
    onLog = () => {},
    onBatch = () => {},
    cancelRef = { current: false },
  } = opts;

  const systemPrompt = buildSystemPrompt(product, { vocab, instructions });
  const batches = chunk(questions, batchSize);
  onLog(`Batching ${questions.length} questions → ${batches.length} batch${batches.length === 1 ? '' : 'es'} of ${batchSize}, concurrency ${concurrency}`, 'info');
  onLog(`System prompt: ${(systemPrompt.length / 1024).toFixed(1)} KB (RKB embedded) · model ${model}`, 'info');
  onLog(`Answer vocabulary (${vocab.source}): ${labelsOf(vocab).join(' · ')}`, 'info');

  let done = 0, ok = 0, failed = 0;
  const t0 = performance.now();

  const batchWorker = async (batch, idx) => {
    if (cancelRef.current) return { idx, rows: [], error: 'canceled' };
    const bStart = performance.now();
    try {
      const parsed = await callClaudeBatch({ systemPrompt, questions: batch, model });
      const ms = Math.round(performance.now() - bStart);
      const rows = batch.map((q) => {
        const raw = parsed.find((x) => x && Number(x.id) === q.id);
        return normalizeRow(q, raw, vocab, onLog);
      });
      done++; ok++;
      onLog(`Batch ${idx + 1}/${batches.length} done · ${ms}ms · ${batch.length} rows`, 'ok');
      onBatch({ done, total: batches.length, ms, ok, failed });
      return { idx, rows };
    } catch (e) {
      const ms = Math.round(performance.now() - bStart);
      done++; failed++;
      onLog(`Batch ${idx + 1}/${batches.length} FAILED · ${e.message} — rows flagged Gap`, 'err');
      onBatch({ done, total: batches.length, ms, ok, failed });
      const rows = batch.map((q) => gapFallback(q, `batch ${idx + 1} failed: ${e.message}`, vocab));
      return { idx, rows, error: e.message };
    }
  };

  // Preflight: run the first batch alone so it writes the prompt cache.
  // Without this, a wide initial fan-out has N batches all trying to write
  // the same ~35K-token cache entry, which balloons input-token-per-minute
  // usage and trips 429s on Tier 2. After the preflight the rest run at
  // full concurrency and hit the cache read path (10× cheaper, ~2× faster).
  const batchResults = new Array(batches.length);
  if (batches.length > 1) {
    onLog('Warming prompt cache with batch 1…', 'info');
    batchResults[0] = await batchWorker(batches[0], 0);
    if (cancelRef.current) throw new Error('canceled');

    const rest = batches.slice(1);
    const restResults = await runPool(rest, concurrency, (b, i) => batchWorker(b, i + 1));
    for (let i = 0; i < rest.length; i++) batchResults[i + 1] = restResults[i];
  } else if (batches.length === 1) {
    batchResults[0] = await batchWorker(batches[0], 0);
  }

  if (cancelRef.current) throw new Error('canceled');

  const elapsed = Math.round(performance.now() - t0);
  const allRows = batchResults.flatMap((b) => b.rows);
  const tally = emptyTally(vocab);
  for (const r of allRows) {
    if (r.status in tally) tally[r.status]++;
  }
  // Roll the per-label tally up into the three color roles the UI reports on.
  const byRole = { met: 0, partial: 0, gap: 0 };
  for (const [label, count] of Object.entries(tally)) byRole[roleOf(vocab, label)] += count;
  const summary = Object.entries(tally).map(([l, c]) => `${l} ${c}`).join(' / ');
  onLog(`All batches complete · ${elapsed}ms wall · ${summary}`, 'ok');

  return {
    rows: allRows,
    tally,
    met: byRole.met, partial: byRole.partial, gap: byRole.gap,
    total: allRows.length,
    batches: batches.length,
    elapsedMs: elapsed,
  };
}
